import { useMemo, useState } from 'preact/hooks'
import type { LandingPageDesignAnswerDto, LandingPageDesignQuestionDto } from '@/types/landingPage'

interface DesignQuestionsFormProps {
    questions: LandingPageDesignQuestionDto[]
    /** Which round this is, and the cap — shown so the user knows this is not open-ended. */
    round?: number
    maxRounds?: number
    submitting?: boolean
    onSubmit: (answers: LandingPageDesignAnswerDto[], note: string | null) => void
    /** Build now with whatever direction exists, and stop asking. */
    onLetWebsterDecide: () => void
}

/**
 * The design questions Webster asks before building a page.
 *
 * A question carrying options renders as multi-select chips; one without renders as a
 * free-text field. That is driven by the data rather than a flag, because the shape of
 * the question is what decides how it can be answered — not every answer comes from a
 * chip, and forcing one would make the open questions unanswerable.
 *
 * Nothing is pre-selected, including a recommendation: the badge says what Webster would
 * pick, but picking is still the user's.
 */
export function DesignQuestionsForm({
    questions,
    round,
    maxRounds,
    submitting = false,
    onSubmit,
    onLetWebsterDecide,
}: DesignQuestionsFormProps) {
    // Keyed by question TEXT, not by position. The questions array is replaced whenever
    // the page refetches or a new round arrives, so an index captured at click time can
    // point at a different question by submit time — which would silently file the user's
    // answer against the wrong question. Text is the stable identity here, and it is also
    // what the payload is labelled with, so the two cannot drift apart.
    const [chosen, setChosen] = useState<Record<string, string[]>>({})
    const [typed, setTyped] = useState<Record<string, string>>({})
    const [note, setNote] = useState('')

    const toggleOption = (question: string, option: string) => {
        setChosen((prev) => {
            const current = prev[question] ?? []
            return {
                ...prev,
                [question]: current.includes(option)
                    ? current.filter((o) => o !== option)
                    : [...current, option],
            }
        })
    }

    const answers = useMemo<LandingPageDesignAnswerDto[]>(
        () =>
            questions
                .map((q) => {
                    const picked = chosen[q.Question] ?? []
                    const free = (typed[q.Question] ?? '').trim()
                    const answer = [picked.join(', '), free].filter(Boolean).join(' — ')
                    return { Question: q.Question, Answer: answer || null }
                })
                .filter((a) => a.Answer),
        [questions, chosen, typed],
    )

    const hasAnswered = answers.length > 0 || note.trim().length > 0

    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-4">
            <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    A couple of quick things before I build
                </p>
                {round != null && maxRounds != null && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        Round {round} of at most {maxRounds}
                    </p>
                )}
            </div>

            {questions.map((q, i) => {
                const options = q.Options ?? []
                const picked = chosen[q.Question] ?? []
                return (
                    <div key={`${q.Question}-${i}`} className="space-y-2">
                        <p className="text-sm text-slate-700 dark:text-slate-200">{q.Question}</p>

                        {options.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {options.map((option) => {
                                    const isPicked = picked.includes(option)
                                    const isRecommended = q.Recommended === option
                                    return (
                                        <button
                                            key={option}
                                            type="button"
                                            aria-pressed={isPicked}
                                            disabled={submitting}
                                            onClick={() => toggleOption(q.Question, option)}
                                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors disabled:opacity-50 ${
                                                isPicked
                                                    ? 'border-primary-600 bg-primary-600 text-white'
                                                    : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-primary-500'
                                            }`}
                                        >
                                            {option}
                                            {isRecommended && (
                                                <span
                                                    className={`text-[10px] uppercase tracking-wider ${
                                                        isPicked ? 'text-white/80' : 'text-slate-400 dark:text-slate-500'
                                                    }`}
                                                >
                                                    suggested
                                                </span>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        ) : (
                            <input
                                type="text"
                                aria-label={q.Question}
                                value={typed[q.Question] ?? ''}
                                disabled={submitting}
                                onInput={(e) =>
                                    setTyped((prev) => ({ ...prev, [q.Question]: (e.target as HTMLInputElement).value }))
                                }
                                placeholder="Your answer…"
                                className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 disabled:opacity-50"
                            />
                        )}
                    </div>
                )
            })}

            <div className="space-y-2">
                <label htmlFor="design-questions-note" className="block text-xs text-slate-500 dark:text-slate-400">
                    Anything else I should know? (optional)
                </label>
                <textarea
                    id="design-questions-note"
                    value={note}
                    rows={2}
                    disabled={submitting}
                    onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
                    className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 disabled:opacity-50"
                />
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
                <button
                    type="button"
                    disabled={submitting}
                    onClick={onLetWebsterDecide}
                    className="text-xs text-slate-500 dark:text-slate-400 underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-50"
                >
                    Let Webster decide
                </button>
                <button
                    type="button"
                    disabled={submitting || !hasAnswered}
                    onClick={() => onSubmit(answers, note.trim() || null)}
                    className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                    {submitting ? 'Building…' : 'Build my page'}
                </button>
            </div>
        </div>
    )
}
